import axios from "axios";
import { backend_url } from "../../constants"; // Assuming your backend URL is stored here
import type { QuickLink } from "../../types/quickLink";
import { AXIOS_CONFIG } from "../api";

const API_URL = `${backend_url}/api/management/quicklinks/`;

/**
 * Get a list of quick links.
 *
 * @returns A list of quick links.
 */
export const getQuickLinks = async (): Promise<QuickLink[]> => {
  try {
    const response = await axios.get(API_URL, {
      ...AXIOS_CONFIG,
    });

    return response.data.content; // Assuming response contains the quick links array
  } catch (error) {
    console.error("Error fetching quick links:", error);
    throw error;
  }
};

/**
 * Create a new quick link.
 *
 * @param linkData The data for the new quick link.
 * @returns The created quick link.
 */
export const createQuickLink = async (
  linkData: Omit<QuickLink, "id" | "user">
): Promise<QuickLink> => {
  try {
    const response = await axios.post(API_URL, linkData, {
      ...AXIOS_CONFIG,
    });

    return response.data; // Assuming response contains the newly created quick link
  } catch (error) {
    console.error("Error creating quick link:", error);
    throw error;
  }
};

/**
 * Update an existing quick link.
 *
 * @param linkId The ID of the quick link to update.
 * @param linkData The updated quick link data.
 * @returns The updated quick link.
 */
export const updateQuickLink = async (
  linkData: Partial<Omit<QuickLink, "id" | "user">>
): Promise<QuickLink> => {
  try {
    const response = await axios.put(`${API_URL}`, linkData, {
      ...AXIOS_CONFIG,
    });

    return response.data; // Assuming response contains the updated quick link
  } catch (error) {
    console.error("Error updating quick link:", error);
    throw error;
  }
};

/**
 * Delete a quick link.
 *
 * @param linkId The ID of the quick link to delete.
 * @returns The ID of the deleted quick link or null if deletion failed.
 */
export const deleteQuickLink = async (
  linkId: number
): Promise<{ id: number } | null> => {
  try {
    const response = await axios.delete(`${API_URL}`, {
      data: {
        id: linkId,
      },
      ...AXIOS_CONFIG,
    });

    return response.data || null; // Assuming response confirms deletion
  } catch (error) {
    console.error("Error deleting quick link:", error);
    throw error;
  }
};
